import PropTypes from 'prop-types'
import Modal from '@/components/Modal'
import CardConfigure from '@/components/Modal/CardConfigure'
import Button from '@/components/Button'
import InformationCircle from '@/assets/svgs/information-circle.svg?react'
import { DEFAULT_SECONDARY_LABEL, withOrg } from '../constants'

// The boxed, rounded icon treatment reads fine regardless of copy, so both
// presentations share it — only the title and body text are EMS's old versus
// the two-stage wording.
const ICON_CLASS =
  'flex items-center justify-center w-12 h-12 rounded-[10px] shrink-0 bg-theme-95 text-theme-50 [&_svg]:w-6.5 [&_svg]:h-6.5'

// EMS's Approve button is single-stage — there is no second reviewer to route
// to, so it keeps the exact title and copy EMS has always shipped. Only an
// element with `secondaryApproval.required` gets the newer wording naming
// where the submission goes next; without this gate every EMS approval
// wrongly claimed a handoff that never happens.
const present = (elementName, secondaryApproval) =>
  secondaryApproval?.required
    ? {
        title: 'Approve Submission',
        body: withOrg(
          `This confirms the data submission${elementName ? ` for ${elementName}` : ''}. The submission will now be routed to {org} for review.`,
          secondaryApproval.label || DEFAULT_SECONDARY_LABEL
        ),
      }
    : {
        title: 'Approve Request',
        body: 'This action will share data element to the designated recipient. Please confirm all details are correct before proceeding.',
      }

export const ApproveModal = ({ visible, onClose, onConfirm, elementName, secondaryApproval }) => {
  const { title, body } = present(elementName, secondaryApproval)

  return (
    <Modal visible={visible} close={onClose} variant="small" dataTestId="submittal-approve-modal">
      <CardConfigure>
        <CardConfigure.Header>
          <CardConfigure.Header.Title>
            <div className="flex items-center w-full">
              <div className="flex items-center">
                <div className={ICON_CLASS}>
                  <InformationCircle />
                </div>
                <div className="ml-4">
                  <div className="font-avenir-heavy type-header-4 text-grey-30">{title}</div>
                </div>
              </div>
            </div>
          </CardConfigure.Header.Title>
        </CardConfigure.Header>
        <CardConfigure.Body>
          <CardConfigure.Body.Col>
            <p className="font-avenir-medium type-body-2 text-grey-40 mb-0 text-center">{body}</p>
          </CardConfigure.Body.Col>
        </CardConfigure.Body>
        <CardConfigure.Footer>
          <CardConfigure.Footer.Left>
            <Button data-testid="submittal-approve-cancel-btn" round noborder onClick={onClose}>
              Cancel
            </Button>
          </CardConfigure.Footer.Left>
          <CardConfigure.Footer.Right>
            <Button data-testid="submittal-approve-confirm-btn" round onClick={onConfirm}>
              Confirm
            </Button>
          </CardConfigure.Footer.Right>
        </CardConfigure.Footer>
      </CardConfigure>
    </Modal>
  )
}

ApproveModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  // The data element this submittal belongs to, named in the two-stage copy.
  // Absent before the submittal has loaded, where the sentence drops the clause.
  elementName: PropTypes.string,
  // The element's workflow — same shape SubmittalBanner and the status tag
  // resolve against. Gates which presentation renders: see `present` above.
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
}
